import { useState, useEffect, useCallback, useRef } from 'react';
import FlexService from '../services/FlexService';

const ACTIVE_POLL_MS = 3000;
const TERMINAL = new Set(['COMPLETED', 'FAILED', 'CANCELLED', 'TIMEOUT']);

// stable, so a memo that lists it doesn't recompute on every render
const isTerminal = (run) => TERMINAL.has(run.status);

// Instants may arrive as ISO text, epoch milliseconds or epoch seconds (with a fraction)
const toMillis = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * FH-272: the background runs Atom started from chat (a large build part, an import), for the chat to show while
 * they run and to announce when they end. Reads the user's own runs through /api (so it works behind the proxy in
 * every environment), polling every few seconds only while one is running. The runs live on the server, so they are
 * found again after a page reload, a chat switch or signing in again.
 *
 * - runs: the user's recent background runs (every chat), newest first
 * - planStatuses: { planKey: get_plan_status result } for runs of the current chat that build a plan, refreshed on
 *   every poll, so the step tracker moves while the build goes on
 * - refresh(): look again now (after a chat reply, which may have started one)
 * - onFinished(run, planStatus) is called once for each run seen running that has ended
 */
export default function useBackgroundRuns(sessionId, onFinished) {
  const [runs, setRuns] = useState([]);
  const [planStatuses, setPlanStatuses] = useState({});
  const seenRunning = useRef(new Set());
  const announced = useRef(new Set());
  const mountedAt = useRef(Date.now());
  const latestPlan = useRef({});
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;

  const refresh = useCallback(async () => {
    let list;
    try {
      list = await FlexService.listTasks({ background: true });
    } catch (e) {
      return [];
    }
    if (!Array.isArray(list)) return [];
    list = [...list].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    // live plan progress for the runs of the chat on screen
    const planKeys = [...new Set(list
      .filter((r) => !TERMINAL.has(r.status) && r.metadata?.planKey && r.metadata?.sessionId === sessionRef.current)
      .map((r) => r.metadata.planKey))];
    if (planKeys.length) {
      const fetched = {};
      await Promise.all(planKeys.map(async (planKey) => {
        try {
          const res = await FlexService.executeScript({ scriptName: 'get_plan_status', context: { _payload: { planKey } } });
          const status = res?.context?.result || res?.result;
          if (status && status.success !== false) fetched[planKey] = status;
        } catch (e) { /* the next poll tries again */ }
      }));
      if (Object.keys(fetched).length) {
        latestPlan.current = { ...latestPlan.current, ...fetched };
        setPlanStatuses((prev) => ({ ...prev, ...fetched }));
      }
    }
    for (const run of list) {
      if (!TERMINAL.has(run.status)) {
        seenRunning.current.add(run.taskId);
      } else if (!announced.current.has(run.taskId)
          && (seenRunning.current.has(run.taskId) || toMillis(run.createdAt) >= mountedAt.current)) {
        // ended since it was seen running, or started and ended on this page between two looks
        seenRunning.current.delete(run.taskId);
        announced.current.add(run.taskId);
        if (finishedRef.current) finishedRef.current(run, latestPlan.current[run.metadata?.planKey]);
      }
    }
    setRuns(list);
    return list;
  }, []);

  // look once on load and whenever the chat changes; afterwards poll only while something runs
  useEffect(() => { refresh(); }, [refresh, sessionId]);
  const anyRunning = runs.some((r) => !TERMINAL.has(r.status));
  useEffect(() => {
    if (!anyRunning) return undefined;
    const timer = setInterval(refresh, ACTIVE_POLL_MS);
    return () => clearInterval(timer);
  }, [anyRunning, refresh]);

  return { runs, planStatuses, refresh, isTerminal };
}
